import axios from 'axios';
import dayjs from '../../../utils/dayjs';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);

// Use local proxy to avoid CORS issues - setupProxy.js handles the proxying
const NIFI_BASE_URL = '/nifi-api';

let token = null;

// Configure axios instance with reasonable defaults
const axiosInstance = axios.create({
  timeout: 30000,
  withCredentials: false, // Don't send cookies
  headers: {
    'Content-Type': 'application/json',
  }
});

// Add request interceptor
axiosInstance.interceptors.request.use(
  (config) => {
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor
axiosInstance.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Clear token and reset authentication state
function clearToken() {
  token = null;
}

// Login and get JWT token
export async function login(username, password) {
  try {
    clearToken();
    
    const formData = new URLSearchParams();
    formData.append('username', username);
    formData.append('password', password);

    const response = await axiosInstance.post(
      `${NIFI_BASE_URL}/access/token`,
      formData.toString(),
      { 
        headers: { 
          'Content-Type': 'application/x-www-form-urlencoded',
          'Accept': '*/*'
        }
      }
    );
    
    if (response.status === 200 || response.status === 201) {
      if (response.data) {
        token = response.data;
        return token;
      } else {
        throw new Error('No token received in response data');
      }
    } else {
      throw new Error(`Unexpected status code: ${response.status}`);
    }
  } catch (error) {
    if (error.response?.status === 201 && error.response?.data) {
      token = error.response.data;
      return token;
    }
    
    if (error.response?.status === 409) {
      clearToken();
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      try {
        const retryFormData = new URLSearchParams();
        retryFormData.append('username', username);
        retryFormData.append('password', password);
        
        const retryResponse = await axiosInstance.post(
          `${NIFI_BASE_URL}/access/token`,
          retryFormData.toString(),
          { 
            headers: { 
              'Content-Type': 'application/x-www-form-urlencoded',
              'Accept': '*/*'
            }
          }
        );
        
        if ((retryResponse.status === 200 || retryResponse.status === 201) && retryResponse.data) {
          token = retryResponse.data;
          return token;
        }
      } catch (retryError) {
        throw new Error(`Authentication failed after token conflict: ${retryError.message}`);
      }
    }
    
    throw new Error(`Failed to authenticate with NiFi: ${error.message}`);
  }
}

function getAuthHeaders() {
  if (!token) throw new Error('Not authenticated - token is null');
  return { 
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json'
  };
}

// Helper function to handle token expiration and conflicts
async function makeAuthenticatedRequest(requestFn, username, password, retryCount = 0) {
  const maxRetries = 2;
  
  try {
    if (!token) {
      await login(username, password);
    }
    return await requestFn();
  } catch (error) {
    if (error.response?.status === 401 || error.response?.status === 409) {
      clearToken();
      
      if (error.response?.status === 409) {
        await new Promise(resolve => setTimeout(resolve, 1000));
      }
      
      if (retryCount < maxRetries) {
        try {
          await login(username, password);
          return await makeAuthenticatedRequest(requestFn, username, password, retryCount + 1);
        } catch (authError) {
          if (retryCount < maxRetries - 1) {
            const waitTime = Math.pow(2, retryCount) * 1000;
            await new Promise(resolve => setTimeout(resolve, waitTime));
            return await makeAuthenticatedRequest(requestFn, username, password, retryCount + 1);
          } else {
            throw new Error(`Authentication failed after ${maxRetries} retries: ${authError.message}`);
          }
        }
      } else {
        throw new Error(`Max retries (${maxRetries}) exceeded for authentication`);
      }
    }
    
    if (error.response?.status === 403) {
      throw new Error(`Permission denied (403). Check if NiFi user has write permissions.`);
    }
    
    throw error;
  }
}

// Fetch system diagnostics (CPU, memory, etc)
export async function getSystemDiagnostics(username, password) {
  return makeAuthenticatedRequest(async () => {
    const response = await axiosInstance.get(
      `${NIFI_BASE_URL}/system-diagnostics`,
      { headers: getAuthHeaders() }
    );
    return response.data;
  }, username, password);
}

// Fetch flow status (queue, processors, etc)
export async function getFlowStatus(username, password) {
  return makeAuthenticatedRequest(async () => {
    const response = await axiosInstance.get(
      `${NIFI_BASE_URL}/flow/status`,
      { headers: getAuthHeaders() }
    );
    return response.data;
  }, username, password);
}

// Fetch all processors in the root process group
export async function getProcessors(username, password) {
  return makeAuthenticatedRequest(async () => {
    // First, get root process group id
    const rootRes = await axiosInstance.get(
      `${NIFI_BASE_URL}/flow/process-groups/root`,
      { headers: getAuthHeaders() }
    );
    const rootId = rootRes.data.processGroupFlow.id;
    
    // Then, get processors
    const procRes = await axiosInstance.get(
      `${NIFI_BASE_URL}/process-groups/${rootId}/processors`,
      { headers: getAuthHeaders() }
    );
    return procRes.data;
  }, username, password);
}

// Get connection/queue information
export async function getConnections(username, password, processGroupId = 'root') {
  return makeAuthenticatedRequest(async () => {
    const response = await axiosInstance.get(
      `${NIFI_BASE_URL}/process-groups/${processGroupId}/connections`,
      { headers: getAuthHeaders() }
    );
    return response.data;
  }, username, password);
}

// Get controller services
export async function getControllerServices(username, password) {
  return makeAuthenticatedRequest(async () => {
    const response = await axiosInstance.get(
      `${NIFI_BASE_URL}/flow/controller/controller-services`,
      { headers: getAuthHeaders() }
    );
    return response.data;
  }, username, password);
}

// Helper function to check if we're authenticated
export function isAuthenticated() {
  return token !== null;
}

// Helper function to clear authentication
export function logout() {
  clearToken();
}

// Force re-authentication (useful after NiFi restarts)
export async function forceReauth(username, password) {
  clearToken();
  await login(username, password);
  return token;
}

// Test connection function
export async function testConnection(username, password) {
  try {
    await getSystemDiagnostics(username, password);
    return { success: true, message: 'Successfully connected to NiFi' };
  } catch (error) {
    return { success: false, message: error.message };
  }
}

// Get detailed processor information with status from all process groups
export async function getProcessorDetails(username, password) {
  return makeAuthenticatedRequest(async () => {
    // First, get root process group id
    const rootRes = await axiosInstance.get(
      `${NIFI_BASE_URL}/flow/process-groups/root`,
      { headers: getAuthHeaders() }
    );
    const rootId = rootRes.data.processGroupFlow.id;
    
    // Get the flow to see all process groups
    const flow = rootRes.data.processGroupFlow.flow;
    const processGroups = flow.processGroups || [];
    
    
    let allProcessors = { processors: [] };
    let allConnections = { connections: [] };
    
    // Get processors and connections from root level
    try {
      const rootProcRes = await axiosInstance.get(
        `${NIFI_BASE_URL}/process-groups/${rootId}/processors`,
        { headers: getAuthHeaders() }
      );
      allProcessors.processors.push(...(rootProcRes.data.processors || []));
      } catch (error) {
        // No processors at root level
      }
      
      try {
        const rootConnRes = await axiosInstance.get(
          `${NIFI_BASE_URL}/process-groups/${rootId}/connections`,
          { headers: getAuthHeaders() }
        );
        allConnections.connections.push(...(rootConnRes.data.connections || []));
      } catch (error) {
        // No connections at root level
      }
      
      for (const processGroup of processGroups) {
        const pgId = processGroup.id;
        
        try {
          const procRes = await axiosInstance.get(
            `${NIFI_BASE_URL}/process-groups/${pgId}/processors`,
            { headers: getAuthHeaders() }
          );
          allProcessors.processors.push(...(procRes.data.processors || []));
        } catch (error) {
          // Error fetching processors
        }
        
        try {
          const connRes = await axiosInstance.get(
            `${NIFI_BASE_URL}/process-groups/${pgId}/connections`,
            { headers: getAuthHeaders() }
          );
          allConnections.connections.push(...(connRes.data.connections || []));
        } catch (error) {
          // Error fetching connections
        }
      }
    
    return {
      processors: allProcessors,
      connections: allConnections
    };
  }, username, password);
}

// Get flow files from all connections (queues) in all process groups using proper NiFi API
export async function getFlowFiles(username, password) {
  return makeAuthenticatedRequest(async () => {
    try {
      // First, get root process group id and flow structure
      const rootRes = await axiosInstance.get(
        `${NIFI_BASE_URL}/flow/process-groups/root`,
        { headers: getAuthHeaders() }
      );
      const rootId = rootRes.data.processGroupFlow.id;
      const flow = rootRes.data.processGroupFlow.flow;
      const processGroups = flow.processGroups || [];
      let allConnections = [];
      
      try {
        const rootConnRes = await axiosInstance.get(
          `${NIFI_BASE_URL}/process-groups/${rootId}/connections`,
          { headers: getAuthHeaders() }
        );
        const rootConnections = rootConnRes.data.connections || [];
        allConnections.push(...rootConnections);
      } catch (error) {
        // No connections at root level
      }
      
      for (const processGroup of processGroups) {
        const pgId = processGroup.id;
        
        try {
          const connRes = await axiosInstance.get(
            `${NIFI_BASE_URL}/process-groups/${pgId}/connections`,
            { headers: getAuthHeaders() }
          );
          const connections = connRes.data.connections || [];
          allConnections.push(...connections);
        } catch (error) {
          // Error fetching connections
        }
      }
      
      const flowFileResults = allConnections
        .filter(conn => {
          const status = conn.status?.aggregateSnapshot;
          const queuedFiles = status?.flowFilesQueued || parseInt(status?.queuedCount) || 0;
          return queuedFiles > 0;
        })
        .map(conn => {
          const status = conn.status?.aggregateSnapshot;
          const queuedFiles = status?.flowFilesQueued || parseInt(status?.queuedCount) || 0;
          const queuedSize = status?.queuedSize || status?.bytesQueued || '0 bytes';
          
          return {
            connectionId: conn.id,
            connectionName: conn.name,
            source: conn.source,
            destination: conn.destination,
            queuedFiles: queuedFiles,
            queuedSize: queuedSize,
            flowFiles: [],
            success: true,
            note: 'Flowfile listing not available due to server permissions'
          };
        });
      
      return {
        connections: allConnections,
        flowFileDetails: flowFileResults
      };
    } catch (error) {
      throw error;
    }
  }, username, password);
}

// BulletinDTO uses TimeAdapter: "HH:mm:ss z" (e.g. "09:44:14 UTC") — time only, date = today.
const NIFI_BULLETIN_TIME_ONLY = /^(\d{1,2}):(\d{2}):(\d{2})(?:\.(\d{1,3}))?\s+(\S+)$/;

const NIFI_BULLETIN_TIMESTAMP_FORMATS = [
  'MM/DD/YYYY HH:mm:ss.SSS z',
  'MM/DD/YYYY HH:mm:ss.SSS',
  'MM/DD/YYYY HH:mm:ss z',
  'MM/DD/YYYY HH:mm:ss',
  'DD/MM/YYYY HH:mm:ss.SSS z',
  'DD/MM/YYYY HH:mm:ss',
];

function parseNifiBulletinTimeOnly(trimmed) {
  const match = trimmed.match(NIFI_BULLETIN_TIME_ONLY);
  if (!match) return null;

  const hour = match[1].padStart(2, '0');
  const minute = match[2];
  const second = match[3];
  const fractional = match[4];
  const zoneToken = match[5];
  const zoneUpper = zoneToken.toUpperCase();

  const time =
    fractional != null
      ? `${hour}:${minute}:${second}.${fractional.padEnd(3, '0').slice(0, 3)}`
      : `${hour}:${minute}:${second}`;

  const todayLocal = dayjs().format('MM/DD/YYYY');
  const todayUtc = dayjs.utc().format('YYYY-MM-DD');

  if (zoneUpper === 'UTC' || zoneUpper === 'GMT' || zoneUpper === 'Z') {
    const iso =
      fractional != null
        ? `${todayUtc}T${hour}:${minute}:${second}.${fractional.padEnd(3, '0').slice(0, 3)}Z`
        : `${todayUtc}T${hour}:${minute}:${second}Z`;
    const parsed = dayjs.utc(iso);
    if (parsed.isValid()) return parsed;
  }

  const withDate = `${todayLocal} ${time} ${zoneToken}`;
  const dateFormats =
    fractional != null
      ? ['MM/DD/YYYY HH:mm:ss.SSS z', 'MM/DD/YYYY HH:mm:ss z']
      : ['MM/DD/YYYY HH:mm:ss z'];

  for (const format of dateFormats) {
    const parsed = dayjs(withDate, format, true);
    if (parsed.isValid()) return parsed;
  }

  const localFallback = dayjs(`${dayjs().format('YYYY-MM-DD')}T${hour}:${minute}:${second}`);
  return localFallback.isValid() ? localFallback : null;
}

/**
 * NiFi bulletin-board timestamps use TimeAdapter ("HH:mm:ss z") or full date-time strings.
 * Do not use parseInt() on those strings — it yields 1970 dates.
 */
export function parseNifiBulletinTimestamp(timestamp) {
  if (timestamp == null || timestamp === '') return null;

  if (typeof timestamp === 'number') {
    const ms = timestamp > 1e12 ? timestamp : timestamp * 1000;
    const parsed = dayjs(ms);
    return parsed.isValid() ? parsed : null;
  }

  if (typeof timestamp === 'string') {
    const trimmed = timestamp.trim();

    const timeOnly = parseNifiBulletinTimeOnly(trimmed);
    if (timeOnly) return timeOnly;

    if (/^\d+$/.test(trimmed)) {
      const num = Number(trimmed);
      const ms = num > 1e12 ? num : num * 1000;
      const parsed = dayjs(ms);
      return parsed.isValid() ? parsed : null;
    }

    if (trimmed.includes('T') || /^\d{4}-\d{2}-\d{2}/.test(trimmed)) {
      const iso = dayjs(trimmed);
      if (iso.isValid()) return iso;
    }

    for (const format of NIFI_BULLETIN_TIMESTAMP_FORMATS) {
      const parsed = dayjs(trimmed, format, true);
      if (parsed.isValid()) return parsed;
    }

    const fallback = dayjs(new Date(trimmed));
    return fallback.isValid() ? fallback : null;
  }

  const parsed = dayjs(timestamp);
  return parsed.isValid() ? parsed : null;
}

export function formatNifiBulletinTimestamp(timestamp) {
  const parsed = parseNifiBulletinTimestamp(timestamp);
  if (!parsed) {
    return timestamp != null && timestamp !== '' ? `Invalid date (${timestamp})` : 'Unknown time';
  }
  return parsed.format('DD/MM/YYYY HH:mm:ss');
}

export function nifiBulletinTimestampToMs(timestamp) {
  const parsed = parseNifiBulletinTimestamp(timestamp);
  return parsed ? parsed.valueOf() : 0;
}

// Get bulletin board (error messages and events)
export async function getBulletinBoard(username, password) {
  return makeAuthenticatedRequest(async () => {
    const response = await axiosInstance.get(
      `${NIFI_BASE_URL}/flow/bulletin-board`,
      { headers: getAuthHeaders() }
    );
    return response.data;
  }, username, password);
}

// Get specific process group flow data
export async function getProcessGroupFlow(username, password, processGroupId) {
  return makeAuthenticatedRequest(async () => {
    const response = await axiosInstance.get(
      `${NIFI_BASE_URL}/flow/process-groups/${processGroupId}`,
      { headers: getAuthHeaders() }
    );
    return response.data;
  }, username, password);
}

function getProcessGroupDisplayName(processGroup) {
  return (
    processGroup?.component?.name
    || processGroup?.status?.aggregateSnapshot?.name
    || 'Unnamed'
  );
}

/**
 * Build Ant Design TreeSelect tree data for org root and nested process groups.
 */
export async function buildProcessGroupTree(username, password, rootProcessGroupId, maxDepth = 5) {
  const rootFlow = await getProcessGroupFlow(username, password, rootProcessGroupId);
  const rootName =
    rootFlow?.processGroupFlow?.breadcrumb?.breadcrumb?.name
    || 'Organization';

  async function buildChildNodes(parentId, depth) {
    if (depth >= maxDepth) return [];

    const flowData = await getProcessGroupFlow(username, password, parentId);
    const children = flowData?.processGroupFlow?.flow?.processGroups || [];

    return Promise.all(
      children.map(async (child) => {
        const nested = await buildChildNodes(child.id, depth + 1);
        const node = {
          title: getProcessGroupDisplayName(child),
          value: child.id,
          key: child.id,
        };
        if (nested.length > 0) {
          node.children = nested;
        }
        return node;
      })
    );
  }

  const childNodes = await buildChildNodes(rootProcessGroupId, 0);
  const rootNode = {
    title: rootName,
    value: rootProcessGroupId,
    key: rootProcessGroupId,
  };
  if (childNodes.length > 0) {
    rootNode.children = childNodes;
  }
  return [rootNode];
}

/** Path of display names from tree root to the node matching targetId. */
export function findProcessGroupPathInTree(treeNodes, targetId) {
  if (!targetId || !treeNodes?.length) return [];

  function walk(nodes, path) {
    for (const node of nodes) {
      const nextPath = [...path, node.title];
      if (node.value === targetId || node.key === targetId) {
        return nextPath;
      }
      if (node.children?.length) {
        const found = walk(node.children, nextPath);
        if (found.length) return found;
      }
    }
    return [];
  }

  return walk(treeNodes, []);
}

// Get process group status
export async function getProcessGroup(username, password, processGroupId) {
  return makeAuthenticatedRequest(async () => {
    const response = await axiosInstance.get(
      `${NIFI_BASE_URL}/process-groups/${processGroupId}`,
      { headers: getAuthHeaders() }
    );
    return response.data;
  }, username, password);
}

// Get process group status from flow API (alternative method)
export async function getProcessGroupStatus(username, password, processGroupId) {
  return makeAuthenticatedRequest(async () => {
    const response = await axiosInstance.get(
      `${NIFI_BASE_URL}/flow/process-groups/${processGroupId}/status`,
      { headers: getAuthHeaders() }
    );
    return response.data;
  }, username, password);
}

// Start process group
export async function startProcessGroup(username, password, processGroupId) {
  return makeAuthenticatedRequest(async () => {
    const pgResponse = await axiosInstance.get(
      `${NIFI_BASE_URL}/process-groups/${processGroupId}`,
      { headers: getAuthHeaders() }
    );
    
    const revision = pgResponse.data.revision || { version: 0 };
    
    const response = await axiosInstance.put(
      `${NIFI_BASE_URL}/flow/process-groups/${processGroupId}`,
      {
        disconnectedNodeAcknowledged: true,
        revision: revision,
        id: processGroupId,
        state: 'RUNNING'
      },
      { 
        headers: getAuthHeaders(),
        validateStatus: (status) => status === 200 || status === 201 || status === 202
      }
    );
    
    return { success: true, state: response.data.state, id: response.data.id };
  }, username, password);
}

// Stop process group
export async function stopProcessGroup(username, password, processGroupId) {
  return makeAuthenticatedRequest(async () => {
    const pgResponse = await axiosInstance.get(
      `${NIFI_BASE_URL}/process-groups/${processGroupId}`,
      { headers: getAuthHeaders() }
    );
    
    const revision = pgResponse.data.revision || { version: 0 };
    
    const response = await axiosInstance.put(
      `${NIFI_BASE_URL}/flow/process-groups/${processGroupId}`,
      {
        disconnectedNodeAcknowledged: true,
        revision: revision,
        id: processGroupId,
        state: 'STOPPED'
      },
      { 
        headers: getAuthHeaders(),
        validateStatus: (status) => status === 200 || status === 201 || status === 202
      }
    );
    
    return { success: true, state: response.data.state, id: response.data.id };
  }, username, password);
}

// Empty a single connection queue
export async function emptyQueue(username, password, connectionId) {
  return makeAuthenticatedRequest(async () => {
    const response = await axiosInstance.post(
      `${NIFI_BASE_URL}/flowfile-queues/${connectionId}/drop-requests`,
      {},
      { 
        headers: getAuthHeaders(),
        validateStatus: (status) => status === 200 || status === 201 || status === 202
      }
    );
    return response.data;
  }, username, password);
}

// Empty all queues in a process group
export async function emptyAllQueues(username, password, processGroupId) {
  return makeAuthenticatedRequest(async () => {
    const connectionsData = await axiosInstance.get(
      `${NIFI_BASE_URL}/process-groups/${processGroupId}/connections`,
      { headers: getAuthHeaders() }
    );
    
    const connections = connectionsData.data.connections || [];
    const dropResults = [];
    
    for (const conn of connections) {
      try {
        const result = await axiosInstance.post(
          `${NIFI_BASE_URL}/flowfile-queues/${conn.id}/drop-requests`,
          {},
          { 
            headers: getAuthHeaders(),
            validateStatus: (status) => status === 200 || status === 201 || status === 202
          }
        );
        dropResults.push({ connectionId: conn.id, success: true, data: result.data });
      } catch (error) {
        dropResults.push({ 
          connectionId: conn.id, 
          success: false, 
          error: error.message,
          status: error.response?.status,
          details: error.response?.data
        });
      }
    }
    
    return {
      totalConnections: connections.length,
      results: dropResults,
      successCount: dropResults.filter(r => r.success).length,
      failureCount: dropResults.filter(r => !r.success).length
    };
  }, username, password);
}